import {
  Switch,
  Textfield,
  Textarea,
  ScoreEditor,
  ProcessEditor,
  MultiField,
  Form,
  Number,
  Radio,
  Image,
  PlainTextEditor,
} from '../formFields'

export const activityTimeline = {
  name: 'activityTimeline',
  label: 'Activity Timeline',
  description: 'Displays experience events as cards in a timeline. Events can be filtered by channnels and types.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    Textfield({ name: 'description', label: 'Description' }),
    MultiField({
      name: 'sources',
      label: 'Channel',
    }),
    MultiField({
      name: 'eventTypes',
      label: 'Event Types',
    }),
  ],
  defaultContent: {
    meta: { component: 'activityTimeline' },
    content: {
      title: 'Customer Touchpoints',
      description: 'activity timeline',
      eventTypes: [
        'commerce.productViews',
        'commerce.productListAdds',
        'commerce.purchases',
        'offer.declined',
        'offer.accepted',
        'orderTracking',
        'rateCall',
        'geofencing.entry',
        'geofencing.exit',
        'account.registration',
      ],
      sources: ['web', 'mobile', 'call-center', 'crm', 'cx', 'ar', 'chatbot', 'iot'],
    },
  },
}

export const processFlow = {
  name: 'processFlow',
  label: 'Process Flow',
  description: 'Provides a visual representation of a business process and moves a profile across its different steps.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    Textfield({ name: 'description', label: 'Description' }),
    ProcessEditor({ name: 'steps', label: 'Steps' }),
  ],
  defaultContent: {
    meta: { component: 'processFlow' },
    content: {
      title: 'Process Flow',
      description:
        'Provides a visual representation of a business process and moves a profile across its different steps',
      steps: [
        {
          description: 'Prospect recently onboarded',
          eventType: 'leadOperation.newLead',
          step: 'Lead Generation',
        },
        {
          description: 'Prospect looking for info',
          eventType: 'leadOperation.qualify',
          step: 'Qualify Lead',
        },
        {
          description: 'Develop personalized needs',
          eventType: 'leadOperation.develop',
          step: 'Develop Solution',
        },
        {
          description: 'Develop proposal & complete internal review',
          eventType: 'leadOperation.propose',
          step: 'Develop Proposal',
        },
        {
          description: 'Complete Final Proposal',
          eventType: 'leadOperation.close',
          step: 'Complete Process',
        },
      ],
    },
  },
}

export const profileUpdate = {
  name: 'profileUpdate',
  label: 'Update Profile',
  description:
    'Displays a form which let you update real time customer profile attributes (arrays and collections are not supported).',
  hasChildren: false,
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    Textfield({ name: 'description', label: 'Description' }),
    Form({ name: 'attributes', label: 'Attributes' }),
  ],
  defaultContent: {
    meta: { component: 'profileUpdate' },
    content: {
      title: 'Update Profile Details',
      attributes: [
        {
          name: 'entity.person.name.firstName',
          type: 'text',
          label: 'First name',
          options: '',
        },
        {
          name: 'entity.person.name.lastName',
          type: 'text',
          label: 'Last name',
        },
        {
          name: 'entity.person.birthDate',
          type: 'calendar',
          label: 'Birth date',
        },
        {
          name: 'entity.person.gender',
          type: 'dropdown',
          label: 'Gender',
          options: 'male,female,not_specified,non_specific',
        },
        {
          name: 'entity.testProfile',
          type: 'checkbox',
          label: 'Test Profile',
          options: '',
        },
      ],
      description: 'Update profile details and user preferences',
    },
  },
}

export const customerAI = {
  name: 'customerAI',
  label: 'Customer Scores',
  description:
    'Displays customer scores in radial graphs. Score can be configured as dynamic from real time customer profile or as constant.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    Textfield({ name: 'description', label: 'Description' }),
    ScoreEditor({ name: 'scores', label: 'Scores' }),
  ],
  defaultContent: {
    meta: { component: 'customerAI' },
    content: {
      title: 'Customer Scoring',
      description: 'Displays scores attached to customer',
      scores: [
        {
          title: 'Churn Score',
          risk: 'red',
          attribute: 'entity.$tenantId.individualScoring.churnPrediction',
          description: 'entity.$tenantId.score.lastModified',
        },
        {
          title: 'Propensity Score',
          risk: 'green',
          attribute: 'entity.$tenantId.individualScoring.core.propensityScore',
          description: 'entity.$tenantId.score.lastModified',
        },
        {
          title: 'CSAT Score',
          risk: 'green',
          attribute: 'entity.$tenantId.customerSatisfactionScore.score',
          description: 'entity.$tenantId.score.lastModified',
        },
        {
          title: 'RFM Score',
          risk: 'green',
          attribute: '0.20',
          description: 'entity.$tenantId.score.lastModified',
        },
      ],
    },
  },
}

export const activitySummary = {
  name: 'activitySummary',
  label: 'Activity Summary',
  description: 'Displays customer key indicators (purchases value, products view, number of purchases).',
  hasChildren: false,
  fields: [Textfield({ name: 'title', label: 'Title' }), Textfield({ name: 'description', label: 'Description' })],
  defaultContent: {
    meta: { component: 'activitySummary' },
    content: { title: 'Activity Summary', description: 'Purchase KPIs' },
  },
}

export const conversionFunnel = {
  name: 'conversionFunnel',
  label: 'Conversion Funnel',
  description: 'Displays a conversion funnel (products views, add to cart and purchased) broken down by channel.',
  hasChildren: false,
  fields: [Textfield({ name: 'title', label: 'Title' }), Textfield({ name: 'description', label: 'Description' })],
  defaultContent: {
    meta: { component: 'conversionFunnel' },
    content: { title: 'Conversion Funnel', description: 'Customer Journey Activity' },
  },
}

export const contact = {
  name: 'contact',
  label: 'Contact',
  description: 'Displays a form to contact customer using email, sms or phone. Triggers customEvent in Launch',
  hasChildren: false,
  fields: [Textfield({ name: 'title', label: 'Title' }), Textfield({ name: 'description', label: 'Description' })],
  defaultContent: {
    meta: { component: 'contact' },
    content: { title: 'Contact Customer', description: 'Send message' },
  },
}

export const kiosk = {
  name: 'kiosk',
  label: 'Kiosk',
  hasChildren: false,
  description: 'QR code scanner component to mimic self service pickup kiosk.  Triggers customEvent in Launch',
  fields: [Textfield({ name: 'title', label: 'Title' }), Textfield({ name: 'description', label: 'Description' })],
  defaultContent: {
    meta: { component: 'kiosk' },
    content: { title: 'Self Service Pickup Kiosk', description: 'Scan  QR code to pickup your article' },
  },
}

export const recommendation = {
  name: 'recommendation',
  label: 'Recommendation',
  description:
    'Displays a carousel showing up to 3 offers (using json placement). User acceptance is sent as experience event. Triggers customEvent in Launch',
  hasChildren: false,
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    Textfield({ name: 'description', label: 'Description' }),
    Textarea({ name: 'decisionScope1', label: 'First  decision scope' }),
    Number({ name: 'itemCount1', label: 'Offer to display', min: 0, max: 30 }),
    Textarea({ name: 'decisionScope2', label: 'Second  decision scope' }),
    Number({ name: 'itemCount2', label: 'Offer to display', min: 0, max: 30 }),
    Textarea({ name: 'decisionScope3', label: 'Third  decision scope' }),
    Number({ name: 'itemCount3', label: 'Offer to display', min: 0, max: 30 }),
  ],
  defaultContent: {
    meta: { component: 'recommendation' },
    content: {
      title: 'Next Best Offer',
      description: 'Customer next best offer, using AEP offer decisioning',
      decisionScope1: '',
      itemCount1: 1,
      decisionScope2: '',
      itemCount2: 1,
      decisionScope3: '',
      itemCount3: 1,
    },
  },
}

export const customerSatisfaction = {
  name: 'customerSatisfaction',
  label: 'Customer Satisfaction',
  hasChildren: false,
  description:
    'Displays questions  to get customer feedbacks about product / services. Questions are rated from Strongly Disagree to Strongly Agree. Triggers customEvent in Launch',
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    Textfield({ name: 'description', label: 'Description' }),
    Switch({ name: 'showLogCallSection', label: 'Show log call section ?', defaultChecked: true }),
    MultiField({
      name: 'questions',
      label: 'Questions',
    }),
  ],
  defaultContent: {
    meta: { component: 'customerSatisfaction' },
    content: {
      title: 'Customer Satisfaction Survey',
      description: 'Get inputs from your customer and updates its churn / satisfaction score.',
      showLogCallSection: true,
      questions: [
        'The app/website is appropriate for my shopping needs',
        'Merchandise sold is of highest quality',
        'The merchandise sold is good value for the money',
        'Advertised merchandise in stock',
        'I am very satisfied with the merchandise I purchased',
        'I would recommend this app/website for my friend and family',
      ],
    },
  },
}

export const journeyTrigger = {
  name: 'journeyTrigger',
  label: 'Event Trigger',
  description: 'Displays configured event details with a button to trigger it.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    Textarea({ name: 'description', label: 'Description' }),
    Image({ name: 'thumbnail', label: 'Thumbnail' }),
    Radio({
      name: 'mode',
      label: 'Configuration type',
      options: [
        { label: 'Advanced', value: 'advanced', defaultChecked: true },
        { label: 'Simplified', value: 'simplified', disabled: true },
      ],
    }),
    Textfield({ name: 'event', label: 'Event', hidden: (cmp) => cmp.content.mode === 'advanced' }),
    MultiField({
      name: 'parameters',
      label: 'Parameters',
      hidden: (cmp) => cmp.content.mode === 'advanced',
    }),
    PlainTextEditor({ name: 'payload', label: 'Payload', hidden: (cmp) => cmp.content.mode !== 'advanced' }),
    Switch({
      name: 'joTestModeEnabled',
      label: 'AJO Test Mode',
      tooltip: 'Adds a joTestModeEvent_ prefix to the event ID to allow the application to work with AJO Test Mode',
      defaultChecked: false,
    }),
  ],
  defaultContent: {
    meta: { component: 'journeyTrigger' },
    content: {
      mode: 'advanced',
      joTestModeEnabled: false,
    },
  },
}
export const DEFAULT_COMPONENT = activitySummary.defaultContent
